import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { workoutPlanService } from '../../../services/supabaseService';

const QuickAssignModal = ({ workout, clients, onClose, onAssignComplete }) => {
  const [selectedClients, setSelectedClients] = useState([]);
  const [scheduledDate, setScheduledDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignmentSuccess, setAssignmentSuccess] = useState(false);

  const filteredClients = clients?.filter(client =>
    client?.client?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    client?.client?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const toggleClient = (clientId) => {
    setSelectedClients(prev =>
      prev?.includes(clientId)
        ? prev?.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleAssign = async () => {
    if (selectedClients?.length === 0) return;

    setIsLoading(true);
    try {
      // Assign workout to each selected client
      const assignmentPromises = selectedClients?.map(clientId =>
        workoutPlanService?.assignWorkoutToClient(workout?.id, clientId, scheduledDate)
      );

      await Promise?.all(assignmentPromises);
      
      setAssignmentSuccess(true);
      setTimeout(() => {
        onAssignComplete();
      }, 1500);
    } catch (error) {
      console.error('Error assigning workout:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-card border-b border-border p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Quick Assign Workout</h2>
            <p className="text-sm text-muted-foreground mt-1">{workout?.name}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        {assignmentSuccess ? (
          <div className="p-12 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <Icon name="Check" size={40} className="text-green-600" />
            </motion.div>
            <h3 className="text-xl font-bold text-foreground mb-2">Workout Assigned!</h3>
            <p className="text-muted-foreground">
              Successfully assigned to {selectedClients?.length} client{selectedClients?.length > 1 ? 's' : ''}
            </p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Scheduled Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Scheduled Date
              </label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e?.target?.value)}
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Client Search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Clients ({selectedClients?.length} selected)
              </label>
              <div className="relative mb-3">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Client List */}
              <div className="border border-border rounded-lg max-h-64 overflow-y-auto">
                {filteredClients?.length > 0 ? (
                  <div className="divide-y divide-border">
                    {filteredClients?.map((assignment) => (
                      <label
                        key={assignment?.clientId}
                        className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedClients?.includes(assignment?.clientId)}
                          onChange={() => toggleClient(assignment?.clientId)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {assignment?.client?.fullName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {assignment?.client?.email}
                          </p>
                        </div>
                        {assignment?.client?.fitnessGoals && assignment?.client?.fitnessGoals?.length > 0 && (
                          <div className="flex gap-1">
                            {assignment?.client?.fitnessGoals?.slice(0, 2)?.map((goal, idx) => (
                              <span key={idx} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                                {goal}
                              </span>
                            ))}
                          </div>
                        )}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Icon name="Users" size={40} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No clients found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Select Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedClients(clients?.map(c => c?.clientId))}
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedClients([])}
              >
                Clear Selection
              </Button>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e?.target?.value)}
                placeholder="Add any special instructions or notes for the clients..."
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
              />
            </div>

            {/* Workout Summary */}
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="text-sm font-semibold text-foreground mb-2">Workout Summary</h4>
              <div className="space-y-1 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={14} />
                  <span>{workout?.durationMinutes} minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Target" size={14} />
                  <span className="capitalize">{workout?.difficulty} level</span>
                </div>
                {workout?.workoutPlanExercises && (
                  <div className="flex items-center gap-2">
                    <Icon name="List" size={14} />
                    <span>{workout?.workoutPlanExercises?.length} exercises</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleAssign}
                loading={isLoading}
                disabled={selectedClients?.length === 0}
                fullWidth
              >
                <Icon name="UserPlus" size={18} className="mr-2" />
                Assign to {selectedClients?.length} Client{selectedClients?.length !== 1 ? 's' : ''}
              </Button>
              <Button variant="outline" onClick={onClose} fullWidth>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default QuickAssignModal;